import { Checkbox, DateTimeInput, Field, NumericInput, TextInput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { DateTimeStyle } from 'Components/Layout/Grid/types';
import {
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { getDryMassGridFieldSet } from 'Components/Worksheets/WashWorksheet/BsEnIso/gridFieldSets';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';

let HydrometerWashWashingSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const showSingleReadingSection = useSelector(
    pipe(
      createWorksheetSelector!('Masses.MeasuredWashedConstantDryMass.DryingTray.DryMassReadings'),
      (r) => (r?.length ?? 0) <= 1 && dryMassEntryOption === 'Never'
    )
  );

  return (
    <>
      <ListFieldSection>
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Equipment.WashingSieve"
          queryParams={{ SieveSize: '63 μm' }}
        />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Equipment.GuardSieve"
          queryParams={{ SieveSize: '2 mm' }}
        />
        <Field input={<Checkbox />} name="Preparation.IsEvaporationRequired" />
      </ListFieldSection>

      {/* TODO: everything from this point down is identical in the hydrometer and sieving components,
      refactor to use a shared component. */}
      <NamePrefixModifier appendNamePrefix={'Masses'}>
        <ListFieldSection>
          <NamePrefixModifier appendNamePrefix="MeasuredWashedConstantDryMass">
            <Field input={<WorksheetEquipmentDropdown />} equipmentType="oven" name="Equipment.Oven" />
            <Field input={<TextInput />} name="DryingTray.ContainerLabel" maxLength={20} />
            <Field input={<NumericInput />} name="DryingTray.ContainerMass" />
            {dryMassEntryOption !== 'Never' && (
              <Field input={<DateTimeInput />} name="DryingTray.DateTimePlacedInOvenLocal" />
            )}
            <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
          </NamePrefixModifier>
        </ListFieldSection>
        <NamePrefixModifier appendNamePrefix="MeasuredWashedConstantDryMass">
          {showSingleReadingSection ? (
            <ListFieldSection>
              <Field
                input={<NumericInput />}
                name="DryingTray.DryMassReadings.0.DrySoilAndContainerMass"
                // We do not show the dry mass calculated from the Tare + Dry mass, so we need to show its messages on this field
                additionalMessagePaths={['Masses.DryingTray.0.DrySoilMass']}
              />
            </ListFieldSection>
          ) : (
            <WorksheetGridFieldSection name="DryingTray.DryMassReadings">
              <WorksheetRecordGrid
                name="DryingTray.DryMassReadings"
                orientation="vertical"
                recordProps={{
                  enforceAtLeastOneRecord: true,
                  preventRemove: 'all-but-last',
                  preventCreate: dryMassEntryOption === 'Never',
                  removeConfirm: {
                    confirm: true,
                    confirmTitle: 'Delete Dry Mass Reading',
                    confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                    confirmButtonText: 'Delete',
                  },
                }}
                fieldSet={getDryMassGridFieldSet(dryMassEntryOption, DateTimeStyle.Combined)}
              />
            </WorksheetGridFieldSection>
          )}
        </NamePrefixModifier>
      </NamePrefixModifier>
    </>
  );
};

HydrometerWashWashingSection = withInjectedProps(HydrometerWashWashingSection);

export default HydrometerWashWashingSection;
export { HydrometerWashWashingSection as WashingSection };
